import { MjmlColumn, MjmlSection, MjmlText } from "@faire/mjml-react";
import { InteractiveCartDataType } from "@redotech/redo-model/advanced-flow/type-system/data-types/trackable-types";
import {
  CartLayoutType,
  EmailFormat,
  getButtonSize,
  getLayoutTypeFromColumnCount,
  getPadding,
  ProductSelectionType,
  Section,
  SharedProps,
  Size,
} from "@redotech/redo-model/email-builder";
import {
  CartMutationAction,
  InteractiveCartResponse,
} from "@redotech/redo-model/email-builder/interactive-cart-response";
import * as max from "lodash/max";
import { memo, ReactNode, useEffect, useState } from "react";
import { LineItemButton } from ".";
import { AmpDynamicSelect } from "../amp-utils/amp-dynamic-select";
import { AmpForm } from "../amp-utils/amp-form";
import { AmpList } from "../amp-utils/amp-list";
import { MustacheContext, MustacheSection } from "../amp-utils/amp-variable";
import { InlineEmailButton } from "../button";
import {
  ampImageScale,
  EmailLineItem,
  rootImageSize,
  sizeRatio,
  verticalStackRowPadding,
} from "../email-line-item";
export type InteractiveCartProps = Section.InteractiveCart & SharedProps;

export const BUTTON_SCHEMA_FIELD_NAME = "url";
const CART_LIST_ID = "cart-list";
// const CART_SUMMARY_ID = "cart-summary";
const CHECKOUT_FORM_ID = "checkout-form";
const CART_ACTION_NAME = "cartAction"; // don't use "action" because input name="action" is disallowed

export const AmpEmailInteractiveCart = memo(function AmpEmailInteractiveCart(
  props: InteractiveCartProps & { format: EmailFormat.AMP },
) {
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(
    () => {
      setRefreshKey(refreshKey + 1);
    }, // weird things happen in the preview when changing certain props
    // unless we refresh the component at each change
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...Object.values(props)],
  );

  if (!props.schemaFieldName) throw new Error("schemaFieldName is required");

  const interactiveCart = props.schemaInstance[props.schemaFieldName] as
    | InteractiveCartDataType["Interactive Cart"]
    | undefined;

  if (!interactiveCart?.cart) {
    return null; // we can't just return the static version because it's invalid AMP, e.g., img elements are not allowed
  }

  const baseUrl = interactiveCart.cart.apiUrl;
  const baseEndpoint = `${baseUrl}/widgets/${interactiveCart.cart.widgetSlug}`;

  const customerId = props.schemaInstance.redoCustomerId
    ? `${props.schemaInstance.redoCustomerId}`
    : "";

  const queryParams = new URLSearchParams({
    numItems: props.numberOfProducts.toString(),
    utmCampaign: props.utm.getCampaign() ?? "",
    emailTemplateId: props.template._id,
    cartBlockId: props.blockId ?? "",
    customerId,
  });
  const cartEndpoint = `${baseEndpoint}/email/cart/${encodeURIComponent(
    interactiveCart.cart.id,
  )}?${queryParams}`;

  const checkoutButtonSize = getButtonSize(
    props.checkoutButton.size,
    props.checkoutButton.width,
    props.checkoutButton.height,
  );
  const checkoutButtonPadding = getPadding(
    props.checkoutButton.horizontalPadding,
    props.checkoutButton.verticalPadding,
    props.checkoutButton.padding,
  );

  // exactly estimating the height is futile because of the dynamic nature of the content
  // (e.g., long produc titles, many variants, etc.)
  // but this gets us pretty close
  // amp-img adds a bit of height and I have no idea why
  const ampAdjustment = 6;
  const hrHeight = 1;
  const finalAdjustment = 1; // not sure why this is needed
  const minRowHeight = 158; // text block height when text height > image height
  const baseComputedRowHeight =
    sizeRatio[props.imageSize] * rootImageSize * ampImageScale +
    verticalStackRowPadding * 2 +
    ampAdjustment +
    hrHeight +
    finalAdjustment;
  const rowHeight = max([minRowHeight, baseComputedRowHeight]) ?? minRowHeight;

  const checkoutHeaderHeight = 72;
  const checkoutHeight =
    checkoutButtonSize.height +
    checkoutButtonPadding.top +
    checkoutButtonPadding.bottom +
    checkoutHeaderHeight;

  const buttonSchemaInstance = { url: interactiveCart.cart.checkoutUrl };

  const numRows = Math.ceil(props.numberOfProducts / (props.columns || 1));
  const height =
    rowHeight *
    Math.min(numRows, 3) *
    approximateAmpHeightMultiplier(props.columns || 1);

  const padding = props.sectionPadding;

  return (
    <MjmlSection
      backgroundColor={props.sectionColor}
      key={refreshKey}
      paddingBottom={padding.bottom}
      paddingLeft={padding.left}
      paddingRight={padding.right}
      paddingTop={padding.top}
    >
      <MjmlColumn>
        <MjmlText color={props.textColor} fontFamily={props.fontFamily}>
          <div style={{ position: "relative" }}>
            {/* TODO: CSS grid for column layout */}
            <AmpList<InteractiveCartResponse, "lineItems">
              columns={props.columns as 2 | 3 | 4}
              height={height}
              id={CART_LIST_ID}
              items="lineItems"
              layout="fixed-height"
              maxItems={props.numberOfProducts}
              overflowButton={
                <div
                  style={{
                    backgroundImage: `linear-gradient(to bottom, ${props.sectionColor}00 0, ${props.sectionColor}ff 50%)`,
                    textAlign: "center",
                    color: props.textColor,
                    padding: "8px",
                  }}
                >
                  Show more
                </div>
              }
              singleItem={false}
              src={cartEndpoint as `https://${string}`}
              template={(scope) => (
                <AmpLineItemTemplate
                  baseEndpoint={baseEndpoint}
                  cartProps={props}
                  interactiveCart={{
                    ...interactiveCart,
                    cart: interactiveCart.cart!,
                  }}
                  scope={scope}
                />
              )}
            />
            <AmpList<InteractiveCartResponse, ".">
              height={checkoutHeight}
              id={CHECKOUT_FORM_ID}
              items="."
              layout="fixed-height"
              singleItem
              src={cartEndpoint as `https://${string}`}
              template={(scope) => (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      paddingBottom: "24px",
                      paddingTop: "24px",
                    }}
                  >
                    <div>Subtotal ({scope.var("cartQuantity")})</div>
                    <div>{scope.var("formattedCost")}</div>
                  </div>
                  <InlineEmailButton
                    {...props.checkoutButton}
                    as="a"
                    buttonLink={scope.var("checkoutUrl")}
                    bypassUtm
                    format={props.format}
                    fullWidth
                    horizontalPadding={Size.CUSTOM}
                    isBuilder={props.isBuilder}
                    padding={props.checkoutButton.padding}
                    schemaFieldName={BUTTON_SCHEMA_FIELD_NAME}
                    schemaInstance={buttonSchemaInstance}
                    sectionColor="transparent"
                    team={props.team}
                    template={props.template}
                    utm={props.utm}
                    verticalPadding={Size.CUSTOM}
                  />
                </>
              )}
            />
          </div>
        </MjmlText>
      </MjmlColumn>
    </MjmlSection>
  );
});

export const AmpLineItemTemplate = ({
  baseEndpoint,
  scope,
  cartProps,
  interactiveCart,
}: {
  baseEndpoint: string;
  scope: MustacheContext<InteractiveCartResponse, "lineItems">;
  cartProps: InteractiveCartProps & { format: EmailFormat.AMP };
  interactiveCart: InteractiveCartDataType["Interactive Cart"] & {
    cart: NonNullable<InteractiveCartDataType["Interactive Cart"]["cart"]>;
  };
}) => {
  if (!cartProps.schemaFieldName)
    throw new Error("schemaFieldName is required");

  const formId = `${scope.var("variant.sanitizedId")}-quantity-form`;

  const mutationFormProps = { baseEndpoint, formId, interactiveCart, scope };

  const layoutType = getLayoutTypeFromColumnCount(cartProps.columns);

  /*
   * There are two types of line items that behave slightly differently:
   * 1) recommendations and 2) items already in the cart.
   * Recommendations don't submit when the dropdown changes, but items already in the cart do.
   * Thus, they look almost identical but are wrapped in forms differently.
   */
  return (
    <>
      {/* RECOMMENDATION */}
      <MustacheSection
        context={scope}
        path={"isRecommendation" as const}
        type="if"
      >
        <div style={{ position: "relative" }}>
          <CartMutationForm action="add" {...mutationFormProps}>
            <EmailLineItem
              additionalMetadata={
                <MustacheSection
                  context={scope}
                  path={"variantDimensions" as const}
                  type="foreach"
                >
                  {(subScope) => {
                    return (
                      <AmpDynamicSelect
                        id={`option-${subScope.var("name")}`}
                        name="options" // multiple inputs of same name = array
                        scope={subScope}
                        style={{ width: "100%" }}
                      />
                    );
                  }}
                </MustacheSection>
              }
              alignment={cartProps.alignment}
              format={cartProps.format}
              imageCornerRadius={cartProps.imageCornerRadius}
              imageSize={cartProps.imageSize}
              isBuilder={cartProps.isBuilder}
              item={{
                title: scope.var("variant.product.title"),
                variantTitle: scope.var("variant.title"),
                quantity: scope.var("quantity"),
                price: scope.var("formattedCost"),
                image: { src: scope.var("imageUrl") },
                properties: {},
              }}
              showDummyImages={
                cartProps.isBuilder &&
                cartProps.productSelectionType === ProductSelectionType.DYNAMIC
              }
              showImage={cartProps.showImage ?? true}
              showPrice
              showQuantity={cartProps.showQuantity ?? false}
              showTitle={cartProps.showTitle ?? true}
              showVariant={false}
              stackType={layoutType}
              team={cartProps.team}
              textColor={cartProps.textColor}
              utm={cartProps.utm}
            >
              {(cartProps.showButton ?? true) && (
                <LineItemButton
                  {...cartProps}
                  {...cartProps.lineItemButtons}
                  alignment={
                    layoutType === CartLayoutType.COLUMNS
                      ? cartProps.alignment
                      : cartProps.lineItemButtons.alignment
                  }
                  buttonText="Add to cart"
                  noPadding={layoutType === CartLayoutType.COLUMNS}
                  schemaFieldName={undefined}
                />
              )}
            </EmailLineItem>
          </CartMutationForm>
        </div>
      </MustacheSection>

      {/* ALREADY IN CART */}
      <MustacheSection
        context={scope}
        path={"isRecommendation" as const}
        type="unless"
      >
        <div style={{ position: "relative" }}>
          <EmailLineItem
            additionalMetadata={
              <CartMutationForm action="update" {...mutationFormProps}>
                <MustacheSection
                  context={scope}
                  path={"variantDimensions" as const}
                  type="foreach"
                >
                  {(subScope) => {
                    return (
                      <AmpDynamicSelect
                        id={`option-${subScope.var("name")}`}
                        name="options" // multiple inputs of same name = array
                        onChange={`${formId}.submit`}
                        scope={subScope}
                        style={{ width: "100%" }}
                      />
                    );
                  }}
                </MustacheSection>
              </CartMutationForm>
            }
            alignment={cartProps.alignment}
            format={cartProps.format}
            imageCornerRadius={cartProps.imageCornerRadius}
            imageSize={cartProps.imageSize}
            isBuilder={cartProps.isBuilder}
            item={{
              title: scope.var("variant.product.title"),
              variantTitle: scope.var("variant.title"),
              quantity: scope.var("quantity"),
              price: scope.var("formattedCost"),
              image: { src: scope.var("imageUrl") },
              properties: {},
            }}
            showDummyImages={
              cartProps.isBuilder &&
              cartProps.productSelectionType === ProductSelectionType.DYNAMIC
            }
            showImage={cartProps.showImage ?? true}
            showPrice
            showQuantity={cartProps.showQuantity ?? false}
            showTitle={cartProps.showTitle ?? true}
            showVariant={false}
            stackType={layoutType}
            team={cartProps.team}
            textColor={cartProps.textColor}
            utm={cartProps.utm}
          >
            <CartMutationForm action="remove" {...mutationFormProps}>
              {(cartProps.showButton ?? true) && (
                <LineItemButton
                  {...cartProps}
                  {...cartProps.lineItemButtons}
                  alignment={
                    layoutType === CartLayoutType.COLUMNS
                      ? cartProps.alignment
                      : cartProps.lineItemButtons.alignment
                  }
                  buttonText="Remove"
                  noPadding={layoutType === CartLayoutType.COLUMNS}
                  schemaFieldName={undefined}
                />
              )}
            </CartMutationForm>
          </EmailLineItem>
        </div>
      </MustacheSection>
    </>
  );
};

const LineItemOverlay = (props: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={props.className}
    style={{
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#fffa",
    }}
  >
    {props.children}
  </div>
);

const LoadingSpinner = () => <div className="spinner" />;

const CartMutationForm = (props: {
  baseEndpoint: string;
  formId: string;
  interactiveCart: InteractiveCartDataType["Interactive Cart"] & {
    cart: NonNullable<InteractiveCartDataType["Interactive Cart"]["cart"]>;
  };
  scope: MustacheContext<InteractiveCartResponse, "lineItems">;
  children: React.ReactNode;
  action: CartMutationAction;
}) => (
  <AmpForm
    actionXhr={`${props.baseEndpoint}/cart/add-or-remove`}
    id={props.formId}
    onSubmitError={`${CART_LIST_ID}.refresh,${CHECKOUT_FORM_ID}.refresh`}
    onSubmitSuccess={`${CART_LIST_ID}.refresh,${CHECKOUT_FORM_ID}.refresh`}
    submitError={
      <LineItemOverlay>An error occurred, please try again</LineItemOverlay>
    }
  >
    {/*
      Can't use the AmpForm's submitting/submitSuccess props
      because the spinner will reset its rotation between the two events.
      Instead, we need to use custom CSS
    */}
    <LineItemOverlay className="line-item-loader">
      <LoadingSpinner />
    </LineItemOverlay>
    <input name="cartId" type="hidden" value={props.interactiveCart.cart.id} />
    <input
      name="lineItemId"
      type="hidden"
      value={props.scope.var("sanitizedId")}
    />
    <input
      name="variantId"
      type="hidden"
      value={props.scope.var("variant.sanitizedId")}
    />
    <input
      name="productId"
      type="hidden"
      value={props.scope.var("variant.product.sanitizedId")}
    />
    <input name="quantity" type="hidden" value={props.scope.var("quantity")} />
    <input
      name="storeUrl"
      type="hidden"
      value={props.interactiveCart.cart.storeUrl}
    />
    <input
      name="teamId"
      type="hidden"
      value={props.interactiveCart.cart.teamId}
    />
    <input name={CART_ACTION_NAME} type="hidden" value={props.action} />
    {props.children}
  </AmpForm>
);

/*
 * Can't know the exact height since it's dynamic content, but this
 * at least bumps up the height to the point of not looking weird on load
 */
function approximateAmpHeightMultiplier(numColumns: number) {
  if (numColumns === 2) return 1.5; // big images
  return 1.1;
}
